import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePublicFacility } from '../context/PublicFacilityContext';
import { initGA4, trackEvent } from '../utils/ga4';
import BrandLogo from '../components/BrandLogo';

// Operator/owner login — passwordless, email 6-digit code (two-step).
// Reads ?email= so invite links land here prefilled.
const Login = () => {
  const [params] = useSearchParams();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(params.get('email') || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Password is the ALTERNATE path: email-code stays the default because most
  // operator accounts have no password_hash at all. /auth/login answers a
  // passwordless account with a clear "use a code instead" rather than a 401.
  const [password, setPassword] = useState('');
  const { login, requestCode, verifyCode, isAuthenticated } = useAuth();
  const { facility } = usePublicFacility();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated()) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (facility?.ga4_measurement_id) initGA4(facility.ga4_measurement_id);
  }, [facility]);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const doRequest = async (e) => {
    e?.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Enter your email');
      return;
    }
    setLoading(true);
    const r = await requestCode(email.trim().toLowerCase());
    setLoading(false);
    if (r.success) {
      setStep('code');
      setInfo('If that account exists, a sign-in code has been emailed to you.');
      setCooldown(60);
    } else {
      setError(r.error);
    }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length !== 6) {
      setError('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    const r = await verifyCode(email.trim().toLowerCase(), code.trim());
    setLoading(false);
    if (r.success) {
      trackEvent('login', { method: 'email_code' });
      navigate('/');
    } else {
      setError(r.error);
    }
  };

  const doPasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Enter your email and password');
      return;
    }
    setLoading(true);
    const r = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (r.success) {
      trackEvent('login', { method: 'password' });
      navigate('/');
    } else {
      setError(r.error);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--c-bg)' }}
    >
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <BrandLogo className="mb-4" />
          {/* Logo already carries the mySMS360 wordmark — only show a
              heading for tenant-branded (facility) logins, never the
              redundant product name. */}
          {facility?.name && (
            <h2 className="text-xl font-semibold text-slate-900">{facility.name}</h2>
          )}
          <p className="text-[13px] text-slate-400 mt-1">
            {step === 'password'
              ? 'Sign in with your password'
              : 'Sign in with a code sent to your email'}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-6">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">{error}</div>
          )}
          {info && (
            <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200/80 px-3 py-2.5 text-[13px] text-blue-700">{info}</div>
          )}

          {step === 'password' ? (
            <form onSubmit={doPasswordLogin} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="you@example.com"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Password</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              {/* This IS the password reset. Email-code sign-in works for
                  every operator and always will, so someone locked out gets
                  in with a code and sets a new password in Settings — no
                  second reset-link mechanism doing the same job with worse
                  security. Worded as recovery, because "use an email code"
                  alone doesn't read as the way out when you're stuck. */}
              <button
                type="button"
                onClick={() => { setStep('email'); setError(''); setPassword(''); }}
                className="w-full text-[13px] text-blue-600 hover:text-blue-700 font-medium"
              >
                Forgot your password? Sign in with an email code
              </button>
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                Signing in with a code always works. Once you&rsquo;re in, set a
                new password under Settings &rarr; Users.
              </p>
            </form>
          ) : step === 'email' ? (
            <form onSubmit={doRequest} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="you@example.com"
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending…' : 'Email me a sign-in code'}
              </button>
              {/* Operator host only — a facility-branded login is a tenant's
                  door, and tenants do not sign up as operators. */}
              {!facility?.name && (
                <p className="text-[12px] text-slate-400 text-center">
                  New to mySMS360?{' '}
                  <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-medium">Create your account</Link>
                </p>
              )}
            </form>
          ) : (
            <form onSubmit={doVerify} className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">6-digit code</label>
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className={`${inputCls} text-center tracking-[0.5em] text-lg font-mono`}
                  placeholder="000000"
                  disabled={loading}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Verifying…' : 'Sign in'}
              </button>
              <button
                type="button"
                disabled={cooldown > 0 || loading}
                onClick={doRequest}
                className="w-full text-[13px] text-slate-500 hover:text-slate-700 disabled:opacity-50"
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
            </form>
          )}
        </div>

        {/* NO self-serve signup link. Operator accounts are provisioned by a
            portfolio owner, facility manager, or platform staff — never
            created from this page. Two TENANTS previously found this screen,
            took it for their tenant portal, and signed themselves up as
            facility owners. The tenant line below is the redirect they
            needed. */}
        <div className="mt-4 text-center space-y-2">
          {step !== 'password' && (
            <button
              type="button"
              onClick={() => { setStep('password'); setError(''); setInfo(''); }}
              className="block w-full text-[13px] font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              Sign in with a password instead
            </button>
          )}
          <p className="text-[12px] text-slate-400 leading-relaxed px-2">
            This is the operator sign-in for storage staff. If you rent a unit
            and want to view your account or pay your bill, sign in from your
            storage facility&rsquo;s own website.
          </p>
          <p className="text-[12px] text-slate-400">
            Need an operator account? Ask your facility manager &mdash;
            accounts are set up for you.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
